import React, { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  addMonths,
  formatISO,
  parseISO,
  startOfDay,
  startOfMonth,
} from "date-fns";

// Form Elements
import { CheckBox, DoctorCard, HourPicker, Submit } from "../../FormElements";

// Components
import { ErrorMessage } from "../../ErrorMessage/ErrorMessage";
import { LoadingAnimation } from "../../../LoadingAnimation/LoadingAnimation";
import { LocationDropdown } from "../../FormElements/Dropdown/LocationDropdown/LocationDropdown";
import { DoctorDropdown } from "../../FormElements/Dropdown/DoctorDropdown/DoctorDropdown";
import { GenericModal } from "../../FormElements/Modal/GenericModal/GenericModal";
import { StepLayout } from "../StepLayout/StepLayout";
import { RichTextArea } from "../../RichTextArea/RichTextArea";
import { SvgIcon } from "../../../SvgIcon/SvgIcon";
import { Breadcrumbs } from "../../Breadcrumbs/Breadcrumbs";
import { Calendar } from "../../Calendar/Calendar";
import { Summary } from "../../Summary/Summary";

// Types & Models
import { AppointmentDto } from "../../../../../Api/Model/AppointmentDto.csharp";
import { DoctorDto } from "../../../../../Api/Model/DoctorDto.csharp";
import { LocationDto } from "../../../../../Api/Model/LocationDto.csharp";
import { AppointmentBookingSystem } from "@apiModels/AppointmentBookingSystem.csharp";
import { HelsebokaInfo } from "@apiModels/HelsebokaInfo.csharp";

// Helpers
import { RootDataType } from "../../Helpers/interfaces";
import { FieldStatus } from "../../Helpers/FieldStatus";
import { clear, onSubmit, setStorage } from "../../Helpers/formFunctions";
import { GtmService } from "../../GtmService";
import {
  findRelatedServiceLocationSetup,
  isRelatedServiceEnabled,
} from "../../Helpers/relatedServiceHelper";
import { newDateInOsloTimeZone } from "../../Helpers/timeZoneHelpers";
import { stepHeadings } from "../../stepHeadings";
import {
  FormDispatch,
  FormFields,
  FormReducer,
  FormStateType,
} from "../../Helpers/formTypes";

// Hooks
import { useLabels } from "../../Hooks/useLabels";
import { useFilteredLocations } from "../../Hooks/useFilteredLocations";
import { useFilteredDoctors } from "../../Hooks/useFilteredDoctors";
import { useInterval } from "../../Hooks/useAppointments";
import { useMissingAppointmentsMessage } from "../../Hooks/useNoAppointmentsMessage";
import { usePricingSummary } from "../../Hooks/usePricingSummary";
import { useTimeZoneModal } from "../../Hooks/useTimeZoneModal";
import { useCancellationDeadline } from "../../Hooks/useCancellationDeadline";
import { useMissingAppointments } from "../../Hooks/useMissingAppointments";
import { useRelatedServiceSetups } from "../../Hooks/useRelatedServiceSetups";
import { useBookingContext } from "../../../BookingButton/BookingContext/BookingContext";
import { useLocationDropdown } from "../../Hooks/useLocationAndDoctorSelection";
import classNames from "classnames";

type AppointmentStepType = {
  state: FormStateType;
  dispatch: FormDispatch;
  nextStep: () => void;
  rootData: RootDataType;
};

export type IntervalDay = {
  hasAppointments: boolean;
  date: Date;
};

export type HelsebokaFilter = {
  defragment: boolean;
  treatmentCategoryIds: number[];
  treatmentIds: number[];
  type: "NORMAL" | "VIDEO";
};

export const AppointmentStep: FC<AppointmentStepType> = ({
  state,
  dispatch,
  nextStep,
  rootData,
}) => {
  const { bookingLabels } = useLabels();
  const { filteredLocations, unavailableLocations } = useFilteredLocations(
    rootData.centers,
    state.service
  );
  const { filteredDoctors } = useFilteredDoctors(
    rootData.doctors,
    state.service,
    state.location
  );

  const [isHelsebokaModalOpen, setIsHelsebokaModalOpen] = useState(false);
  const [noAppointmentSelectedError, setNoAppointmentSelectedError] =
    useState(false);

  const errorAreaRef = useRef<HTMLDivElement>(null);

  const scrollToErrorArea = () => {
    errorAreaRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    setTimeout(() => {
      errorAreaRef.current?.focus();
    }, 300);
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  useRelatedServiceSetups(state.service, dispatch);
  const { newBookingFilterValues } = useBookingContext();
  const getHelsebokaFilterValue = (
    helsebokaInfo: HelsebokaInfo
  ): HelsebokaFilter => {
    return {
      defragment: true,
      treatmentIds: [helsebokaInfo.serviceId],
      treatmentCategoryIds: [helsebokaInfo.serviceCategoryId],
      type: "NORMAL",
    };
  };
  const submitHelsebokaModal = () => {
    setIsHelsebokaModalOpen(false);

    if (!!state.appointment.helsebokaInfo) {
      const filter = getHelsebokaFilterValue(state.appointment.helsebokaInfo);
      const uri = encodeURI(
        `https://www.helseboka.app/booking/clinic/${
          state.appointment.helsebokaInfo.clinicId
        }/treatment?timeSlotId=${
          state.appointment.helsebokaInfo.appointmentId
        }&filter=${JSON.stringify(filter)}`
      );
      window.open(uri, "_blank", "noreferrer");
    }
  };
  const updateServiceAppointments = (
    value: boolean,
    relatedServiceId?: number
  ) => {
    updateMonthToCurrentMonth();
    updateDayToFirstAvailable();
    /* Initial state */
    const newState = new Map<number, boolean>(state.showRelatedServicesMap);
    let newBaseFilterState = state.showBaseService;
    /* Logic */
    if (relatedServiceId !== undefined) {
      newState.set(relatedServiceId, value);
      if ([...newState].filter(([k, v]) => v).length === 0) {
        newBaseFilterState = true;
      }
    } else {
      newBaseFilterState = value;
      if (!newBaseFilterState) {
        for (let [key] of newState) {
          newState.set(key, true);
        }
      }
    }
    /* Updating form */
    setValue(FormFields.showBaseService, newBaseFilterState);
    for (let [key, value] of newState) {
      setValue(`${FormFields.showRelatedServices}-${key}`, value);
    }
    /* Updating state */
    dispatch({
      type: FormReducer.update,
      field: FormFields.showBaseService,
      payload: newBaseFilterState,
    });
    dispatch({
      type: FormReducer.update,
      field: FormFields.showRelatedServicesMap,
      payload: newState,
    });
  };

  useMissingAppointments(state.service, state.showRelatedServicesMap, dispatch);

  const [displayMonth, setDisplayMonth] = useState<Date>(
    startOfMonth(newDateInOsloTimeZone())
  );
  const [activeMonth, setActiveMonth] = useState<Date>(
    startOfMonth(newDateInOsloTimeZone())
  );
  const [selectedDay, setSelectedDay] = useState<Date>(
    startOfDay(newDateInOsloTimeZone())
  );

  const [autoQueryRepeat, setAutoQueryRepeat] = useState(0);

  const { secondStepLabels } = bookingLabels!.stepsBookingLabels!;
  const commonLabels = bookingLabels!.commonLabels;

  const { missingAppointmentsMessage: noAppointmentMessage } =
    useMissingAppointmentsMessage(
      state.doctor,
      state.location,
      state.specializationName,
      state.service,
      state.missingAppointments,
      rootData.centers
    );

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    register,
    trigger,
  } = useForm<FormStateType>({
    defaultValues: state,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const {
    onLocationSelect: baseOnLocationSelect,
    onDoctorSelect: baseOnDoctorSelect,
  } = useLocationDropdown(filteredLocations, state, dispatch, setValue);

  useEffect(() => {
    !!state.selectedMonth
      ? handleMonthChange(parseISO(state.selectedMonth))
      : handleMonthChange(startOfMonth(newDateInOsloTimeZone()));

    !!state.selectedDay
      ? handleDayChange(parseISO(state.selectedDay))
      : handleDayChange(startOfDay(newDateInOsloTimeZone()));
  }, []);

  useEffect(() => {
    for (const rs of state.relatedServiceSetups) {
      const relatedServiceLocationSetup = findRelatedServiceLocationSetup(
        rs,
        state.location
      );

      updateServiceAppointments(
        relatedServiceLocationSetup?.showRelatedService ??
          rs.showRelatedService,
        rs.relatedServiceId
      );
    }
  }, [state.location]);

  const {
    interval,
    filteredAppointments,
    loadingAppointments,
    error: appointmentsError,
    warning: appointmentsWarning,
    failedCenters: appointmentsFailedCenters,
    appointmentsForSelectedDay,
  } = useInterval(
    state,
    activeMonth,
    autoQueryNextMonth,
    autoQueryRepeat,
    filteredDoctors,
    selectedDay
  );

  useEffect(() => {
    if (state.appointment.serviceVariantId) {
      setValue(FormFields.appointment, state.appointment);
      dispatch({
        type: FormReducer.update,
        field: FormFields.appointment,
        payload: state.appointment,
      });
    }
  }, []);

  useEffect(() => {
    setStorage(state);
  }, [state]);

  useEffect(() => {
    GtmService.bookingAppointmentsScreenShown(
      state.specializationName,
      state.service.specializationId,
      state.service.bookingName,
      state.service.id,
      state.location.name,
      state.location.id,
      state.doctor.name,
      state.doctor.id,
      newBookingFilterValues?.isBookingFallback,
      newBookingFilterValues?.breadcrumbsShown && state.breadcrumbs.length > 0
    );
  }, []);

  function secondStepSubmit() {
    if (!state.appointment.serviceId) {
      setNoAppointmentSelectedError(true);
      scrollToErrorArea();
      return;
    }
    const center = rootData.centers.find(
      (c) => c.id === state.appointment.centerId
    );
    const doctor = rootData.doctors.find(
      (d) => d.id === state.appointment.doctorId
    );

    GtmService.appointmentStepSubmit(
      state.specializationName,
      state.service.specializationId,
      state.service.bookingName,
      state.service.id,
      center?.name ?? "",
      center?.id ?? "",
      doctor?.name ?? "",
      doctor?.id ?? "",
      state.appointment.startTime,
      newBookingFilterValues?.breadcrumbsShown
    );

    if (
      state.appointment.appointmentBookingSystem ===
      AppointmentBookingSystem.Helseboka
    ) {
      setIsHelsebokaModalOpen(true);
      return;
    }

    return onSubmit(nextStep, errors);
  }

  function handleMonthChange(newMonth: Date) {
    setAutoQueryRepeat(0);
    setDisplayMonth(newMonth);
    setActiveMonth(newMonth);
  }

  function autoQueryNextMonth() {
    setAutoQueryRepeat(autoQueryRepeat + 1);
    setActiveMonth(addMonths(activeMonth, 1));
  }

  function handleDayChange(day: Date) {
    if (day.getTime() == selectedDay?.getTime()) {
      return;
    }

    setSelectedDay(day);
    clear(FormFields.appointment, dispatch, setValue);

    dispatch({
      type: FormReducer.update,
      field: FormFields.selectedDay,
      payload: formatISO(day),
    });
  }

  const onDoctorSelect = (doctor: DoctorDto) => {
    baseOnDoctorSelect(doctor);
  };

  const onLocationSelect = (location: LocationDto) => {
    clear(FormFields.appointment, dispatch, setValue);
    baseOnLocationSelect(location);
  };

  const updateMonthToCurrentMonth = () =>
    handleMonthChange(startOfMonth(newDateInOsloTimeZone()));

  const updateDayToFirstAvailable = () => {
    if (interval.length) {
      const index = interval.findIndex(
        (day) => day.hasAppointments && day.date >= displayMonth
      );
      if (index !== -1) {
        setSelectedDay(interval[index].date);
      }
    }
  };

  function handleTimeChoose(appointment: AppointmentDto) {
    setNoAppointmentSelectedError(false);
    setValue(FormFields.appointment, appointment);
    dispatch({
      type: FormReducer.update,
      field: FormFields.appointment,
      payload: appointment,
    });
  }

  usePricingSummary(state.appointment, dispatch);

  useCancellationDeadline(state.appointment, dispatch);

  useEffect(() => {
    wrapperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const isLoading = loadingAppointments;
  const hasError = !!appointmentsError;

  const isFailedCenterSelected = appointmentsFailedCenters.some(
    (c) => c.id === state.location?.id
  );

  const warningCenters = !!appointmentsFailedCenters.length
    ? appointmentsFailedCenters
    : appointmentsFailedCenters;
  const selectedCentersIds =
    state.location.id && state.location.id != "-1"
      ? state.location.isLocation == true
        ? state.location.centers
        : [state.location.id]
      : [];

  const selectedWarningCenters = !!selectedCentersIds.length
    ? warningCenters.filter((c) => selectedCentersIds.indexOf(c.id) != -1)
    : warningCenters;
  const hasWarning = !!appointmentsWarning && !!selectedWarningCenters.length;

  const warningMessage = appointmentsWarning?.replace(
    "$centers",
    selectedWarningCenters.map((c) => c.name).join(", ")
  );

  const areAppointmentsLoaded =
    interval.length > 0 && appointmentsForSelectedDay.length > 0;

  const { showTimeZoneModal, setShowTimezoneModal } = useTimeZoneModal(
    state,
    dispatch,
    areAppointmentsLoaded,
    hasError
  );

  const submitButtonLabel = secondStepLabels?.nextButtonLabel ?? "Neste";

  const shouldShowBreadcrumbs = state.breadcrumbs.length > 0;

  const appointmentStepCustomHeading =
    bookingLabels?.stepsBookingLabels?.newBookingStepsLabels
      .appointmentsStepHeading;
  const appointmentStepHeading =
    appointmentStepCustomHeading || stepHeadings.appointmentStepHeading;

  const findFirstAvailableDate = (date: Date): Date | null => {
    const availableDates = interval
      .filter((day) => day.hasAppointments)
      .map((day) => day.date);

    if (availableDates.length === 0) return null;

    const closestDate = availableDates.find(
      (d) => d.getTime() >= date.getTime()
    );

    return closestDate || null;
  };

  const firstAvailableDate = findFirstAvailableDate(selectedDay);

  return (
    <div className={classNames("AppointmentStep")} ref={wrapperRef}>
      <StepLayout
        heading={appointmentStepHeading}
        headerAdditionalContent={
          shouldShowBreadcrumbs && (
            <Breadcrumbs breadcrumbs={state.breadcrumbs} />
          )
        }
      >
        <form
          onSubmit={handleSubmit(secondStepSubmit)}
          className="d-flex flex-column"
        >
          <div className="AppointmentStep__dropdownContainer">
            <LocationDropdown
              defaultValue={state.location}
              label={commonLabels.placeDropdownLabel}
              placeholder={commonLabels.placeDropdownPlaceholder}
              onSelect={onLocationSelect}
              locations={{ items: filteredLocations }}
              unavailableLocations={{ items: unavailableLocations }}
              doctor={state.doctor}
              onDoctorSelect={onDoctorSelect}
              service={state.service}
              additionalLabels={commonLabels}
            />
            {state.service.disableDoctorSelection == false &&
              state.location.servicesWithDisabledDoctorSelection.indexOf(
                state.service.id
              ) == -1 && (
                <DoctorDropdown
                  defaultValue={state.doctor}
                  label={secondStepLabels.selectDoctorDropdownLabel}
                  labelSelected={
                    secondStepLabels.selectDoctorDropdownLabelSelected
                  }
                  placeholder={secondStepLabels.selectDoctorDropdownPlaceholder}
                  onSelect={onDoctorSelect}
                  doctors={filteredDoctors}
                  doctor={state.doctor}
                  status={hasError ? FieldStatus.disabled : FieldStatus.active}
                />
              )}
          </div>

          <div className="AppointmentStep__doctorCardScrollTo" />
          {state.doctor.id && state.doctor.id != "-1" && (
            <DoctorCard doctor={state.doctor} centers={rootData.centers} />
          )}

          {state.relatedServiceSetups.map((rs) => {
            const relatedServiceLocationSetup = findRelatedServiceLocationSetup(
              rs,
              state.location
            );

            return (
              isRelatedServiceEnabled(rs, state.location) && (
                <div key={rs.relatedServiceId}>
                  <h2 className="AppointmentStep__relatedServiceHeading">
                    {relatedServiceLocationSetup?.showRelatedServicesLabel ??
                      rs.showRelatedServicesLabel}
                  </h2>
                  <CheckBox
                    name={`${FormFields.showRelatedServices}-${rs.relatedServiceId}`}
                    label={
                      <>
                        {relatedServiceLocationSetup?.showRelatedServicesCheckboxLabel ??
                          rs.showRelatedServicesCheckboxLabel}
                        <SvgIcon
                          iconName="video"
                          className="AppointmentStep__relatedIcon"
                        />
                      </>
                    }
                    register={register}
                    onClick={() =>
                      updateServiceAppointments(
                        !state.showRelatedServicesMap.get(rs.relatedServiceId),
                        rs.relatedServiceId
                      )
                    }
                    disabled={hasError || isLoading}
                    trigger={trigger}
                  />
                </div>
              )
            );
          })}
          {state.service.showRelatedServiceCheckboxLabel &&
            state.relatedServiceSetups.filter((rs) =>
              isRelatedServiceEnabled(rs, state.location)
            ).length > 0 && (
              <CheckBox
                name={FormFields.showBaseService}
                label={state.service.showRelatedServiceCheckboxLabel}
                register={register}
                onClick={() =>
                  updateServiceAppointments(!state.showBaseService)
                }
                disabled={hasError || isLoading}
                trigger={trigger}
              />
            )}

          {!!noAppointmentMessage ? (
            <>
              <RichTextArea
                className={"AppointmentStep__noAppointmentMsg"}
                icon="info"
                content={noAppointmentMessage}
              />
              <Summary state={state} step={3} rootData={rootData} />
            </>
          ) : state.service.availableForOnlineBooking ? (
            <>
              {hasError && (
                <ErrorMessage
                  className="AppointmentStep__errorMessage"
                  message={appointmentsError}
                />
              )}
              {hasWarning && !hasError && (
                <ErrorMessage
                  className="AppointmentStep__errorMessage"
                  message={warningMessage}
                />
              )}

              {!hasError && !isFailedCenterSelected && (
                <>
                  {!loadingAppointments &&
                  interval.length === 0 &&
                  firstAvailableDate === null ? (
                    <RichTextArea
                      className="AppointmentStep__noAppointmentMsg"
                      icon="info"
                      content={
                        autoQueryRepeat === 0
                          ? bookingLabels!.stepsBookingLabels.secondStepLabels
                              .noAppointments
                          : bookingLabels!.stepsBookingLabels.secondStepLabels
                              .noAppointmentsAutoQuery
                      }
                    />
                  ) : (
                    <>
                      <Calendar
                        selectedDay={selectedDay}
                        selectedMonth={displayMonth}
                        onChange={handleDayChange}
                        onMonthChange={handleMonthChange}
                        interval={interval}
                        loading={loadingAppointments}
                        disabled={hasError || isLoading}
                        setSelectedDay={setSelectedDay}
                        noAppointmentsTodayLabel={
                          bookingLabels!.stepsBookingLabels.secondStepLabels
                            .calendarNoAppointmentsTodayLabel
                        }
                        firstAvailableDateButton={
                          bookingLabels!.stepsBookingLabels.secondStepLabels
                            .calendarFirstAvailableDateButton
                        }
                        firstAvailableDate={firstAvailableDate}
                        noAppointmentSelectedError={noAppointmentSelectedError}
                        setNoAppointmentSelectedError={
                          setNoAppointmentSelectedError
                        }
                        autoQueryRepeat={autoQueryRepeat}
                        activeMonth={activeMonth}
                      />
                      {noAppointmentSelectedError && (
                        <div
                          ref={errorAreaRef}
                          className="AppointmentStep__errorMessageContainer"
                          role="alert"
                          tabIndex={-1}
                        >
                          <ErrorMessage
                            className="AppointmentStep__errorMessage"
                            message={
                              commonLabels.noAppointmentSelectedError ??
                              "Velg et tidspunkt"
                            }
                            onClose={() => setNoAppointmentSelectedError(false)}
                          />
                        </div>
                      )}
                      {selectedDay && (
                        <HourPicker
                          availableAppointments={appointmentsForSelectedDay}
                          selectedAppointment={state.appointment}
                          selectedDay={selectedDay!}
                          calendarLoading={loadingAppointments}
                          handleTimeChange={handleTimeChoose}
                          doctors={rootData.doctors}
                          centers={rootData.centers}
                          availableDays={filteredAppointments.map(
                            (a) => new Date(a.startTime)
                          )}
                          goToNextAvailableDay={handleDayChange}
                          selectedDoctor={state.doctor}
                          selectedLocation={state.location}
                          selectedService={state.service}
                          relatedServiceSetups={state.relatedServiceSetups}
                          firstAvailableDate={firstAvailableDate}
                        />
                      )}
                    </>
                  )}
                </>
              )}

              {isHelsebokaModalOpen && (
                <GenericModal
                  onClose={() => setIsHelsebokaModalOpen(false)}
                  onSubmit={submitHelsebokaModal}
                  isOpen={isHelsebokaModalOpen}
                  heading={
                    !!secondStepLabels?.helsebokaRedirectModalHeading
                      ? secondStepLabels.helsebokaRedirectModalHeading
                      : "Fullfør din bestilling på Helseboka"
                  }
                  description={
                    !!secondStepLabels?.helsebokaRedirectModalIntro
                      ? secondStepLabels.helsebokaRedirectModalIntro
                      : "Nettbestilling til Sandvika Nevrosenter gjøres med BankID på helseboka.app"
                  }
                  buttonLabel={
                    !!secondStepLabels?.helsebokaRedirectModalButton
                      ? secondStepLabels.helsebokaRedirectModalButton
                      : "Fortsett til Helseboka"
                  }
                />
              )}
              <Summary state={state} step={3} rootData={rootData} />
              <Submit
                className="AppointmentStep__submitButton"
                label={secondStepLabels?.nextButtonLabel}
                status={!isLoading ? FieldStatus.active : FieldStatus.disabled}
              >
                {isLoading ? (
                  <LoadingAnimation text={submitButtonLabel} />
                ) : (
                  submitButtonLabel
                )}
              </Submit>
            </>
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: noAppointmentMessage,
              }}
            />
          )}
        </form>
        <GenericModal
          onClose={() => {
            setShowTimezoneModal(false);
          }}
          isOpen={showTimeZoneModal}
          heading={secondStepLabels.timeZoneModalHeader}
          description={secondStepLabels.timeZoneModalDescription}
          buttonLabel={secondStepLabels.timeZoneModalButtonLabel}
        />
      </StepLayout>
    </div>
  );
};
